import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ArticleRow, GroupRow, Measure, PeriodQuery, UnitQuantity } from "../../api/reports";
import { reportsApi } from "../../api/reports";
import { formatNumber } from "../../lib/format";
import { useToast } from "../Toast";
import { StabloRegistar } from "./StabloRegistar";
import type { StabloRed, StabloSort } from "./StabloRegistar";

type Props = {
  redovi: GroupRow[];
  ukupno: { quantity: Measure; otherUnits: UnitQuantity[]; salesValue: Measure; ruc: Measure };
  upit: PeriodQuery;
  locale: string;
  /**
   * `false` kad se iz perioda ne sme čitati rast ili pad — period prelazi presek izvora.
   *
   * Dolazi od stranice, jer tabela ne zna period: septembar 2026 nosi 22 dana a poredi se sa punim
   * mesecom. Oba broja ostaju, smer se ne piše.
   */
  tumaci?: boolean;
};

type Nadgrupa = {
  ime: string;
  nerazvrstano: boolean;
  grupe: GroupRow[];
  kolicina: Measure;
  /** Ono što je traka prodala van kilograma — ne sabira se u tonažu nego se imenuje. */
  druge: UnitQuantity[];
  vrednost: Measure;
  ruc: Measure;
};

/**
 * Prodaja po grupama proizvoda: nadgrupa, grupe pod njom, artikli na klik.
 *
 * <p>Zatečena tabela je sva tri nivoa crtala isto, a pri otvaranju grupe usred tabele je niklo
 * DRUGO zaglavlje; svaka ćelija je nosila tekući broj, prošlogodišnji i čip u boji, pa je red bio
 * visok sto tačaka a na ekran je stalo šest redova. Raspored je zato prešao na `StabloRegistar`:
 * jedno zaglavlje, nivo se čita po veličini, a udeo u marži nosi traka.
 *
 * <p>Nijedna mera nije sklonjena — RUC, količina i vrednost i dalje nose i prošlu godinu i smer,
 * a komadi i dalje stoje imenovani pored tonaže.
 *
 * <p>Međuzbir nadgrupe se računa IZ REDOVA koji se vide, nikad zasebnim upitom: zbir koji ne
 * odgovara onome što stoji pod njim gori je od nepostojećeg.
 */
export function ProizvodiTabela({ redovi, ukupno, upit, locale, tumaci }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  // Nadgrupe su otvorene ZATEČENO — tabela se otvara kao spisak grupa, kao i do sada; sklapa se
  // namerno. Grupe su zatvorene, jer bi inače svaka tražila svoje artikle sa servera.
  const [sklopljene, setSklopljene] = useState<Set<string>>(new Set());
  const [otvorene, setOtvorene] = useState<Set<string>>(new Set());
  const [artikli, setArtikli] = useState<Record<string, ArticleRow[]>>({});
  const [ucitava, setUcitava] = useState<string | null>(null);
  // PODRAZUMEVANO SE NE REĐA: redosled sa servera je redosled štampanog izveštaja, a čitalac koji
  // ga gleda godinama mora da nađe svoj red na mestu na kom ga očekuje. Ređanje je na klik.
  const [sort, setSort] = useState<StabloSort>(null);
  const [opadajuce, setOpadajuce] = useState(true);

  const nadgrupe = useMemo(() => poredjaj(grupisi(redovi, t), sort, opadajuce), [redovi, t, sort, opadajuce]);
  const ukupanRuc = ukupno.ruc.current;

  const sortiraj = useCallback(
    (k: StabloSort) => {
      if (k === sort) setOpadajuce((o) => !o);
      else {
        setSort(k);
        // Brojke se gledaju od najveće, imena azbučno.
        setOpadajuce(k !== "ime");
      }
    },
    [sort],
  );

  const otvori = useCallback(
    async (kljuc: string) => {
      const jeNadgrupa = !kljuc.includes("|");
      if (jeNadgrupa) {
        setSklopljene((pre) => {
          const sled = new Set(pre);
          if (sled.has(kljuc)) sled.delete(kljuc);
          else sled.add(kljuc);
          return sled;
        });
        return;
      }

      const bioOtvoren = otvorene.has(kljuc);
      setOtvorene((pre) => {
        const sled = new Set(pre);
        if (bioOtvoren) sled.delete(kljuc);
        else sled.add(kljuc);
        return sled;
      });
      // Grupa koju smo već otvarali ne pita server dvaput.
      if (bioOtvoren || artikli[kljuc]) return;

      const deo = kljuc.split("|");
      setUcitava(kljuc);
      try {
        const odgovor = await reportsApi.salesArticles(upit, deo[0], deo[1]);
        // Odgovor nosi grupu za koju je tražen: spor odgovor ne sme da sleti pod red koji je
        // čovek u međuvremenu otvorio.
        if (`${odgovor.supergroup}|${odgovor.group}` === kljuc) {
          setArtikli((pre) => ({ ...pre, [kljuc]: odgovor.articles }));
        }
      } catch (e) {
        toast.error((e as Error).message);
        setOtvorene((pre) => {
          const sled = new Set(pre);
          sled.delete(kljuc);
          return sled;
        });
      } finally {
        setUcitava((k) => (k === kljuc ? null : k));
      }
    },
    [artikli, otvorene, toast, upit],
  );

  /** Šta je otvoreno: svaka nadgrupa koja nije sklopljena, plus grupe koje su izričito otvorene. */
  const otvoreni = useMemo(() => {
    const sve = new Set(otvorene);
    for (const n of nadgrupe) if (!sklopljene.has(n.ime)) sve.add(n.ime);
    return sve;
  }, [nadgrupe, otvorene, sklopljene]);

  const stablo: StabloRed[] = useMemo(
    () =>
      nadgrupe.map((n) => ({
        kljuc: n.ime,
        ime: n.ime,
        udeo: udeo(n.ruc.current, ukupanRuc),
        mere: [
          { tumaci, sada: n.ruc.current, pre: n.ruc.previous },
          { tumaci, sada: n.kolicina.current, pre: n.kolicina.previous, jedinica: "kg", decimala: 0 },
          { tumaci, sada: n.vrednost.current, pre: n.vrednost.previous },
        ],
        dodatak: uzJedinice(n.druge, locale),
        deca: n.grupe.map((g) => {
          const kljuc = `${g.supergroup}|${g.group}`;
          const ucitani = artikli[kljuc];
          return {
            kljuc,
            ime: g.group || "/",
            udeo: udeo(g.ruc.current, ukupanRuc),
            mere: [
              { tumaci, sada: g.ruc.current, pre: g.ruc.previous },
              { tumaci, sada: g.quantity.current, pre: g.quantity.previous, jedinica: "kg", decimala: 0 },
              { tumaci, sada: g.salesValue.current, pre: g.salesValue.previous },
            ],
            dodatak: uzJedinice(g.otherUnits ?? [], locale),
            mozeDaSeOtvori: true,
            prazno: t("report.articles.empty", "Za ovu grupu nema artikala u periodu."),
            deca: ucitani?.map((a) => ({
              kljuc: `${kljuc}|${a.code}`,
              ime: a.name,
              oznaka: a.code,
              udeo: udeo(a.ruc.current, ukupanRuc),
              mere: [
                { tumaci, sada: a.ruc.current, pre: a.ruc.previous },
                { tumaci, sada: a.quantity.current, pre: a.quantity.previous, jedinica: "kg", decimala: 0 },
                { tumaci, sada: a.salesValue.current, pre: a.salesValue.previous },
              ],
            })),
          };
        }),
      })),
    [artikli, locale, nadgrupe, ukupanRuc],
  );

  if (redovi.length === 0) {
    return (
      <div className="empty" style={{ padding: 18 }}>
        {t("report.emptyProducts", "Za izabrani period nema prodaje.")}
      </div>
    );
  }

  return (
    <StabloRegistar
      kolone={[
        t("report.col.supergroup", "Nadgrupa"),
        t("report.col.ruc", "RUC"),
        t("report.col.quantity", "Količina"),
        t("report.col.value", "Vrednost"),
      ]}
      naslovUdela={t("report.col.shareRuc", "Udeo RUC")}
      sort={sort}
      opadajuce={opadajuce}
      naSortiranje={sortiraj}
      redovi={stablo}
      otvoreni={otvoreni}
      naOtvaranje={otvori}
      ucitava={ucitava}
      zbir={{
        kljuc: "__ukupno",
        ime: t("report.total", "Ukupno"),
        udeo: ukupanRuc === 0 ? null : 100,
        mere: [
          { tumaci, sada: ukupno.ruc.current, pre: ukupno.ruc.previous },
          { tumaci, sada: ukupno.quantity.current, pre: ukupno.quantity.previous, jedinica: "kg", decimala: 0 },
          { tumaci, sada: ukupno.salesValue.current, pre: ukupno.salesValue.previous },
        ],
        dodatak: uzJedinice(ukupno.otherUnits ?? [], locale),
      }}
      locale={locale}
    />
  );
}

/** Udeo u ukupnoj marži; bez ukupnog nema udela, a nula nije odgovor. */
function udeo(deo: number, ukupno: number): number | null {
  if (!ukupno) return null;
  return (deo / ukupno) * 100;
}

/**
 * Ono što je prodato van kilograma — imenovano, nikad pribrojeno.
 *
 * <p>Prazno kad takve prodaje nema, da red ne dobije prazan treći red.
 */
function uzJedinice(druge: UnitQuantity[], locale: string): string | undefined {
  const sa = druge.filter((u) => u.current !== 0 || u.previous !== 0);
  if (sa.length === 0) return undefined;
  return sa
    .map(
      (u) =>
        `+ ${formatNumber(u.current, locale, { maximumFractionDigits: 0 })} ${u.unit.toLowerCase()}` +
        (u.previous ? ` · ${formatNumber(u.previous, locale, { maximumFractionDigits: 0 })}` : ""),
    )
    .join(" · ");
}

function grupisi(redovi: GroupRow[], t: (k: string, d: string) => string): Nadgrupa[] {
  const po = new Map<string, Nadgrupa>();
  for (const r of redovi) {
    const ime = r.unclassified ? t("report.unclassified", "nerazvrstano") : r.supergroup;
    let n = po.get(ime);
    if (!n) {
      n = {
        ime,
        nerazvrstano: r.unclassified,
        grupe: [],
        kolicina: { current: 0, previous: 0, index: null },
        druge: [],
        vrednost: { current: 0, previous: 0, index: null },
        ruc: { current: 0, previous: 0, index: null },
      };
      po.set(ime, n);
    }
    n.grupe.push(r);
    // Količine van kilograma se sabiraju PO JEDINICI, nikad međusobno ni sa tonažom.
    for (const u of r.otherUnits ?? []) {
      const pre = n.druge.find((x) => x.unit === u.unit);
      if (pre) {
        pre.current += u.current;
        // NEPOZNATO SE NE SABIRA U NULU. Ako bilo kom redu prošla godina nije uvezena, ni zbir
        // nadgrupe nije poznat — nula bi tvrdila da te godine nije prodato ništa.
        pre.previous =
          pre.previous === null || u.previous === null ? null : pre.previous + u.previous;
      } else n.druge.push({ ...u });
    }
    saberi(n.kolicina, r.quantity);
    saberi(n.vrednost, r.salesValue);
    saberi(n.ruc, r.ruc);
  }
  return [...po.values()];
}

function saberi(u: Measure, r: Measure) {
  u.current += r.current ?? 0;
  // Nepoznato se ne sabira u nulu: ako prethodni period nije uvezen, ni zbir nadgrupe nije poznat.
  // Preskočiti takav red dao bi delimičan zbir koji izgleda kao pun.
  u.previous = u.previous === null || r.previous === null ? null : u.previous + r.previous;
}

/**
 * Ređa nadgrupe i grupe unutar svake nadgrupe po istoj meri.
 *
 * <p>Tabela je grupisana, pa ređanje samo nadgrupa ostavlja grupe u zatečenom redosledu i izgleda
 * kao da klik radi pola posla.
 *
 * <p>Vraća NOVE nizove: ređanje na licu mesta bi menjalo niz koji je `useMemo` dobio od pozivaoca,
 * pa bi se pri sledećem iscrtavanju ređao već poređan — i zatečeni redosled sa servera, koji je
 * redosled štampanog izveštaja, ne bi se mogao vratiti.
 */
function poredjaj(nadgrupe: Nadgrupa[], sort: StabloSort, opadajuce: boolean): Nadgrupa[] {
  if (sort === null) return nadgrupe;
  const znak = opadajuce ? -1 : 1;
  const poImenu = (a: string, b: string) => znak * a.localeCompare(b);
  // Redosled mera je redosled kolona: 0 RUC, 1 količina, 2 vrednost.
  const meraNadgrupe = (n: Nadgrupa) =>
    sort === 0 ? n.ruc.current : sort === 1 ? n.kolicina.current : n.vrednost.current;
  const meraGrupe = (g: GroupRow) =>
    sort === 0 ? g.ruc.current : sort === 1 ? g.quantity.current : g.salesValue.current;

  return nadgrupe
    .map((n) => ({
      ...n,
      grupe: [...n.grupe].sort((a, b) =>
        sort === "ime" ? poImenu(a.group, b.group) : znak * (meraGrupe(a) - meraGrupe(b)),
      ),
    }))
    .sort((a, b) => (sort === "ime" ? poImenu(a.ime, b.ime) : znak * (meraNadgrupe(a) - meraNadgrupe(b))));
}
